import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, User, ArrowLeft } from "lucide-react";
import axios from "axios";
import Navbar from "./UrjabioNavbar";
import { useNavigate } from "react-router-dom";

const UrjabioProfile = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const User_Id = localStorage.getItem("User_Id");

        if (!User_Id) {
          setError("User ID not found. Please log in again.");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `https://api.livestockify.com/api/urjabio/users/${User_Id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data) {
          setUserData(response.data);
        } else {
          setError("No user data found.");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const bgStyles = (
    <style>{`
      .urjabio-dash .ub-bg-layer {
        position: fixed;
        inset: 0;
        z-index: 0;
        pointer-events: none;
        overflow: hidden;
        background:
          radial-gradient(ellipse 80% 60% at 10% 20%, rgba(52, 199, 89, 0.16), transparent 55%),
          radial-gradient(ellipse 70% 50% at 90% 10%, rgba(10, 92, 51, 0.1), transparent 50%),
          radial-gradient(ellipse 60% 45% at 70% 90%, rgba(52, 199, 89, 0.12), transparent 55%),
          linear-gradient(165deg, #f4fbf6 0%, #eef7f1 42%, #e7f3ec 100%);
      }
      .urjabio-dash .ub-blob {
        position: absolute;
        border-radius: 40% 60% 55% 45%;
        filter: blur(4px);
        opacity: 0.32;
        will-change: transform;
      }
      .urjabio-dash .ub-blob-a {
        width: 28rem;
        height: 28rem;
        top: -6rem;
        left: -5rem;
        background: radial-gradient(circle at 30% 30%, #7ee2a0, #34c759 55%, transparent 70%);
        animation: ub-dash-drift-a 14s ease-in-out infinite;
      }
      .urjabio-dash .ub-blob-b {
        width: 24rem;
        height: 24rem;
        right: -4rem;
        top: 22%;
        background: radial-gradient(circle at 60% 40%, #a8e6bf, #0a5c33 60%, transparent 72%);
        animation: ub-dash-drift-b 18s ease-in-out infinite;
      }
      .urjabio-dash .ub-blob-c {
        width: 20rem;
        height: 20rem;
        bottom: -4rem;
        left: 32%;
        background: radial-gradient(circle at 50% 50%, #c8f0d4, #2fa84f 50%, transparent 70%);
        animation: ub-dash-drift-c 16s ease-in-out infinite;
      }
      .urjabio-dash .ub-grid-fade {
        position: absolute;
        inset: 0;
        background-image:
          linear-gradient(rgba(10, 92, 51, 0.035) 1px, transparent 1px),
          linear-gradient(90deg, rgba(10, 92, 51, 0.035) 1px, transparent 1px);
        background-size: 48px 48px;
        mask-image: radial-gradient(ellipse 75% 65% at 50% 40%, black 25%, transparent 78%);
      }
      @keyframes ub-dash-drift-a {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        50% { transform: translate(36px, 28px) rotate(8deg); }
      }
      @keyframes ub-dash-drift-b {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        50% { transform: translate(-30px, 40px) rotate(-6deg); }
      }
      @keyframes ub-dash-drift-c {
        0%, 100% { transform: translate(0, 0) scale(1); }
        50% { transform: translate(24px, -20px) scale(1.08); }
      }
      @media (prefers-reduced-motion: reduce) {
        .urjabio-dash .ub-blob { animation: none !important; }
      }
    `}</style>
  );

  if (loading) {
    return (
      <div className="urjabio-dash relative flex min-h-screen items-center justify-center text-slate-600">
        {bgStyles}
        <div className="ub-bg-layer" aria-hidden>
          <div className="ub-blob ub-blob-a" />
          <div className="ub-blob ub-blob-b" />
          <div className="ub-blob ub-blob-c" />
          <div className="ub-grid-fade" />
        </div>
        <p className="relative z-10">Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="urjabio-dash relative flex min-h-screen items-center justify-center text-red-600">
        {bgStyles}
        <div className="ub-bg-layer" aria-hidden>
          <div className="ub-blob ub-blob-a" />
          <div className="ub-blob ub-blob-b" />
          <div className="ub-blob ub-blob-c" />
          <div className="ub-grid-fade" />
        </div>
        <p className="relative z-10">{error}</p>
      </div>
    );
  }

  return (
    <div className="urjabio-dash relative min-h-screen text-slate-900">
      {bgStyles}
      <div className="ub-bg-layer" aria-hidden>
        <div className="ub-blob ub-blob-a" />
        <div className="ub-blob ub-blob-b" />
        <div className="ub-blob ub-blob-c" />
        <div className="ub-grid-fade" />
      </div>

      <div className="relative z-50">
        <Navbar />
      </div>

      <div className="relative z-10">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center pl-6 pr-5 pt-6 font-semibold text-slate-800 sm:pl-12 md:pl-20"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to dashboard
        </button>

        <div className="mx-auto max-w-6xl p-6 pt-4 sm:pt-6">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="flex min-h-[380px] flex-col items-center rounded-2xl border border-white/70 bg-white/85 p-10 shadow-sm backdrop-blur-sm">
              <div className="flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-b from-[#34C759] to-[#0a5c33]">
                <User className="h-14 w-14 text-white" />
              </div>

              <h2 className="mt-4 text-2xl font-semibold text-gray-900">
                {userData?.userFirstname} {userData?.userLastname}
              </h2>

              <p className="text-sm text-gray-500">{userData?.roleType}</p>
            </div>

            <div className="min-h-[380px] rounded-2xl border border-white/70 bg-white/85 p-10 shadow-sm backdrop-blur-sm">
              <h3 className="mb-6 text-left text-2xl font-semibold text-gray-900">
                Personal Information
              </h3>

              <div className="grid grid-cols-1 gap-y-6 text-sm text-gray-700 sm:grid-cols-2">
                <div>
                  <p className="flex items-center gap-2 font-medium">
                    <User className="h-4 w-4 text-black" /> First Name
                  </p>
                  <p className="mt-2 pl-6 text-left text-gray-600">
                    {userData?.userFirstname || "—"}
                  </p>
                </div>

                <div>
                  <p className="flex items-center gap-2 font-medium">
                    <User className="h-4 w-4 text-black" /> Last Name
                  </p>
                  <p className="mt-2 pl-6 text-left text-gray-600">
                    {userData?.userLastname || "—"}
                  </p>
                </div>

                <div>
                  <p className="flex items-center gap-2 font-medium">
                    <Mail className="h-4 w-4 text-black" /> Email ID
                  </p>
                  <p className="mt-2 pl-6 text-left text-gray-600">
                    {userData?.userEmail || "—"}
                  </p>
                </div>

                <div>
                  <p className="flex items-center gap-2 font-medium">
                    <Phone className="h-4 w-4 text-black" /> Phone Number
                  </p>
                  <p className="mt-2 pl-6 text-left text-gray-600">
                    {userData?.userPhoneno || "—"}
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <p className="flex items-center gap-2 font-medium">
                    <MapPin className="h-4 w-4 text-black" /> Address
                  </p>
                  <p className="mt-2 pl-6 text-left text-gray-600">
                    {userData?.address || "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrjabioProfile;
